import { motion, useReducedMotion } from "framer-motion";
import { useApi } from "@/lib/useApi";
import { api } from "@/lib/api";
import type { JourneyStep, JourneyTrack } from "@/lib/api";

interface Props {
  onOpenScheme: (id: string) => void;
}

/**
 * Chapter I — "Three Roads Out of Day One".
 *
 * Each track is a founder's road. Cards styled as parchment dispatches
 * with a wax-seal roman numeral; steps read like a serialised index so
 * the reader feels they're tracing a route, not scrolling a grid.
 */

const ROMANS = ["I", "II", "III", "IV", "V"];

export function Journey({ onOpenScheme }: Props) {
  const { data } = useApi(() => api.journeys(), []);
  const reduce = useReducedMotion();
  if (!data) return null;

  return (
    <section id="journey" className="relative max-w-6xl mx-auto px-5 py-24">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-10">
        <div>
          <p className="section-eyebrow mb-2">chapter · one</p>
          <h2 className="section-title">
            Three <span className="font-serif italic text-amber-300">roads</span> out of day one.
          </h2>
          <p className="text-cream-100/60 mt-3 max-w-xl text-sm">
            Pick the road that sounds like your day. Each milestone opens the
            actual scheme or portal behind it — no dead ends, no marketing.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.3em] text-cream-100/50">
          <span className="h-px w-6 bg-amber-400/40" />
          serialised · folio {data.tracks.length}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {data.tracks.map((t, i) => (
          <motion.div
            key={t.id}
            initial={reduce ? false : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.45, delay: i * 0.08 }}
          >
            <TrackCard track={t} roman={ROMANS[i] ?? String(i + 1)} onOpenScheme={onOpenScheme} />
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function TrackCard({
  track, roman, onOpenScheme,
}: {
  track: JourneyTrack; roman: string; onOpenScheme: (id: string) => void;
}) {
  const tint =
    track.accent === "saffron" ? { glow: "rgba(255,183,3,0.18)", seal: "#ffb703", text: "#ffca3a" }
  : track.accent === "accent"  ? { glow: "rgba(230,57,70,0.18)", seal: "#e63946", text: "#ffb703" }
                               : { glow: "rgba(42,157,143,0.16)", seal: "#2a9d8f", text: "#6a994e" };

  return (
    <article
      className="group relative h-full rounded-[1.5rem] border border-amber-400/15 bg-plum-900/45 overflow-hidden
                 hover:border-amber-400/35 transition-colors
                 shadow-[0_22px_60px_-28px_rgba(0,0,0,0.7)]"
    >
      {/* Wash behind seal */}
      <div
        aria-hidden
        className="absolute -top-10 -right-10 w-48 h-48 rounded-full blur-3xl opacity-80"
        style={{ background: `radial-gradient(circle, ${tint.glow}, transparent 70%)` }}
      />

      {/* Wax-seal roman numeral */}
      <div
        aria-hidden
        className="absolute top-5 right-5 w-11 h-11 rounded-full flex items-center justify-center font-serif italic text-lg
                   border border-amber-400/40 bg-plum-950/60 text-cream-50"
        style={{ boxShadow: `inset 0 0 0 2px ${tint.seal}30, 0 0 14px ${tint.glow}` }}
      >
        {roman}
      </div>

      <div className="relative p-6 md:p-7">
        <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-cream-100/55">
          road · {track.id}
        </div>
        <h3
          className="font-display text-[1.5rem] leading-tight font-semibold mt-1.5"
          style={{ color: tint.text }}
        >
          {track.title}
        </h3>
        <p className="text-[13px] text-cream-100/60 mt-1.5 mb-5 leading-relaxed">
          {track.tagline}
        </p>

        <ol className="relative">
          {/* vertical guide rail */}
          <span
            aria-hidden
            className="absolute left-[14px] top-2 bottom-2 w-px bg-gradient-to-b from-amber-400/40 via-amber-400/10 to-transparent"
          />
          {track.steps.map((s, idx) => (
            <Step
              key={s.title}
              step={s}
              idx={idx}
              tint={tint}
              onOpenScheme={onOpenScheme}
            />
          ))}
        </ol>
      </div>
    </article>
  );
}

function Step({
  step, idx, tint, onOpenScheme,
}: {
  step: JourneyStep;
  idx: number;
  tint: { glow: string; seal: string; text: string };
  onOpenScheme: (id: string) => void;
}) {
  return (
    <li
      onClick={() => (step.scheme ? onOpenScheme(step.scheme) : step.url && window.open(step.url, "_blank"))}
      className="group/step relative flex items-start gap-3 py-2 pl-8 -ml-1 rounded-lg cursor-pointer
                 hover:bg-white/[0.03] transition-colors"
    >
      <span
        aria-hidden
        className="absolute left-0 top-2.5 shrink-0 w-7 h-7 rounded-full flex items-center justify-center
                   text-[11px] font-mono font-semibold border border-amber-400/30 bg-plum-950/80"
        style={{ color: tint.text, boxShadow: `inset 0 0 0 1px ${tint.seal}28` }}
      >
        {String(idx + 1).padStart(2, "0")}
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-display text-[13.5px] font-semibold text-cream-50 leading-snug flex items-center gap-1.5">
          {step.title}
          <span className="font-serif italic text-amber-300/60 text-sm opacity-0 group-hover/step:opacity-100 transition-opacity">
            →
          </span>
        </div>
        <div className="text-[12px] text-cream-100/55 leading-snug mt-0.5">
          {step.summary}
        </div>
      </div>
    </li>
  );
}
